import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import Select from "react-select";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import DeleteModal from "../../../Components/Common/DeleteModal";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MobileCategoryForm from "../component/MobileCategoryForm";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

// Options for the status filter
const statusOptions = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
];

const CategoryList = () => {
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showColumnChooser, setShowColumnChooser] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filter states
  const [filters, setFilters] = useState({
    name: "",
    Status: null,
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [categoryType, setCategoryType] = useState("category");

  // Delete modal states
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const toggleModal = (item) => {
    setSelectedDeleteItem(item);
    setIsOpen(!isOpen);
  };
  const columns = [
    {
      name: "S.N",
      selector: (row) => row.index,
      sortable: true,
      width: "75px",
    },
    {
      name: "Category Name",
      selector: (row) => row.name,
      sortable: true,

      wrap: true,
    },
    {
      name: "Parent Category",
      selector: (row) => row.parentName || "None",
      sortable: true,
      width: "25%",
      minWidth: "150px",
      wrap: true,
    },
    {
      name: "Cost Center",
      selector: (row) => row.costCenterName || "None",
      sortable: true,

      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <span className={`badge ${row.isActive ? "bg-success" : "bg-danger"}`}>
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Action",
      selector: (row) => (
        <div>
          <ButtonGroup size="sm">
            <Link to={`/category-detail/${row.id}`}>
              <Button color="btn btn-soft-success" className="btn-sm">
                <i className="bx bx-show" />
              </Button>
            </Link>
            <Link to={`/category/update/${row.id}`}>
              <Button
                color="btn btn-soft-warning"
                className="btn-sm gap-1 ms-1"
                size="sm"
              >
                <i className="ri-edit-line" />
              </Button>
            </Link>
            <Button
              className="btn btn-soft-danger btn-sm gap-1 ms-1"
              color="danger"
              onClick={() => handleDeleteClick(row)}
              size="sm"
            >
              <i className="ri-delete-bin-5-line" />
            </Button>
          </ButtonGroup>
        </div>
      ),
      center: true,
    },
  ];

  const fetchCategories = async (
    pageNumber = 1,
    size = pageSize,
    currentFilters = debouncedFilters
  ) => {
    try {
      setLoading(true);
      const response = await CategoryServices.maincategory({
        ...currentFilters,
        pageNumber,
        pageSize: size,
      });

      const indexedData = response.items.map((item, index) => ({
        ...item,
        index: (pageNumber - 1) * size + index + 1,
        parentName: null, // or fetch parent category if needed
      }));

      setCategoryList(indexedData);
      setTotalRows(response.totalCount);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  // Debounce filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1); // Reset to first page when filters change
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Update IsCatering filter based on categoryType
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      IsCatering: categoryType === "catering" ? true : null,
    }));
  }, [categoryType]);

  // Fetch data when page, size, or filters change
  useEffect(() => {
    fetchCategories(currentPage, pageSize);
  }, [currentPage, pageSize, debouncedFilters]);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, name: e.target.value }));
  };
  

  const handleStatusChange = (selectedOption) => {
    setFilters((prev) => ({ ...prev, Status: selectedOption?.value ?? null }));
  };

  // Delete modal handlers
  const toggleDelete = () => {
    setIsOpenDelete(!isOpenDelete);
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await CategoryServices.deletecategory(selectedDeleteItem.id);
        fetchCategories(currentPage, pageSize);
        toast.success("Category deleted successfully");
      } catch (error) {
        console.error("Error deleting category:", error);
        toast.error(
          error?.response?.data?.message || "Failed to delete category"
        );
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };
  const toggleColumnChooser = () => setShowColumnChooser(!showColumnChooser);

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* <BreadCrumb title="Category List" pageTitle="Category Management" /> */}
        <BreadCrumb
          title="Category List"
          // breadcrumbItems={[
          //   {
          //     title: (
          //       <Button className="btn btn-soft-warning">
          //         + Create Category
          //       </Button>
          //     ),
          //     link: "/category/add",
          //   },
          // ]}
          pageTitle="Category Management"
        />
      </div>

      {/* <Row className="mb-3">
        <Col className="d-flex align-items-end justify-content-end">
          <ButtonGroup>
            <Button
              color={
                categoryType === "category" ? "primary" : "outline-primary"
              }
              onClick={() => setCategoryType("category")}
            >
              Menu
            </Button>
            <Button
              color={
                categoryType === "catering" ? "primary" : "outline-primary"
              }
              onClick={() => setCategoryType("catering")}
            >
              Catering 
            </Button>
          </ButtonGroup>
        </Col>
      </Row> */}

      <Row className="">
        <Col xs={12} lg={4} className="mb-2 ">
          <InputGroup>
            <Input
              placeholder="Search by category name"
              value={filters.name}
              onChange={handleFilterChange}
            />
          </InputGroup>
        </Col>
        <Col xs={7} lg={3} className="mb-2 ">
          <Select
            options={statusOptions}
            value={statusOptions.find(
              (option) => option.value === filters.Status
            )}
            onChange={handleStatusChange}
            placeholder="Select Status"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Col>
        <Col xs={5} lg={5}>
          <Link to="/category/create" className="d-lg-block d-md-block d-none">
            <button className="btn btn-success">+ Add Category</button>
          </Link>
          <button
            className="btn btn-success d-lg-none d-md-none d-block"
            onClick={() => toggleModal()}
          >
            + Add Category
          </button>
        </Col>
      </Row>
      <div className="d-lg-block d-md-block d-none">
        <CustomDataTable
          responsive
          striped
          pagination
          fixedHeader
          persistTableHead
          progressPending={loading}
          customStyles={customStyles}
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={currentPage}
          paginationPerPage={pageSize}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressComponent={
            <div className="my-3">
              <Triangle
                visible={true}
                height="80"
                width="80"
                color="#5B71B9"
                ariaLabel="triangle-loading"
              />
              <h5 className="mt-1">Loading...</h5>
            </div>
          }
          columns={columns}
          data={categoryList}
        />
      </div>
      <div className="d-block d-md-none d-lg-none">
        {loading ? (
          <div className="my-5 d-flex justify-content-center">
            <Triangle
              visible={true}
              height="60"
              width="60"
              color="#5B71B9"
              ariaLabel="triangle-loading"
            />
          </div>
        ) : (
          <>
            {categoryList?.map((item, index) => (
              <Card
                key={item.id || index}
                className="mb-3 shadow-sm d-block d-md-none d-lg-none"
                onClick={(e) => {
                  e.stopPropagation(); // prevents parent click handlers

                  navigate(`/category-detail/${item?.id}`);
                }}
              >
                <CardBody>
                  <Row>
                    <Col xs={10}>
                      <h6 className="mb-2">Category: {`${item.name}`}</h6>
                      <h6 className="mb-2 text-muted">
                        Parent Category: {item?.parentName}
                      </h6>
                      <h6 className="mb-2 text-muted">
                        Cost Center: {item?.costCenterName}
                      </h6>
                      <span
                        className={`badge ${
                          item.isActive ? "bg-success" : "bg-danger"
                        }`}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>{" "}
                    </Col>
                    <Col xs={2}>
                      <div>
                        <Dropdown
                          isOpen={item.dropdownOpen || false}
                          toggle={(e) => {
                            e.stopPropagation(); // prevents parent click handlers

                            setCategoryList((prev) =>
                              prev.map(
                                (customer) =>
                                  customer.id === item.id
                                    ? {
                                        ...customer,
                                        dropdownOpen: !customer.dropdownOpen,
                                      }
                                    : { ...customer, dropdownOpen: false } // close all others
                              )
                            );
                          }}
                          direction="down"
                        >
                          <DropdownToggle
                            color="primary"
                            outline
                            className="btn-sm  shadow-sm rounded-circle"
                          >
                            <i className="ri-more-2-fill"></i>
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem
                              onClick={(e) => {
                                e.stopPropagation(); // prevents parent click handlers
                                toggleModal(item); // opens your modal with the item
                              }}
                            >
                              <i className="ri-edit-line text-warning me-2"></i>{" "}
                              Edit
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => {
                                e.stopPropagation(); // prevents parent click handlers
                                handleDeleteClick(item.id);
                              }}
                            >
                              <i className="ri-delete-bin-5-line text-danger me-2"></i>{" "}
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))}
          </>
        )}
      </div>

      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDelete}
      />
      <MobileCategoryForm
        isOpen={isOpen}
        toggle={toggleModal}
        selectedItem={selectedDeleteItem}
      />
    </div>
  );
};

export default CategoryList;
